/**
 * TabIndex Controller
 * 모달이나 드롭다운이 열릴 때 외부 요소의 탭 인덱스를 관리하는 유틸리티
 */
export class TabIndexController {
    constructor() {
        this.originalTabIndexes = new Map();
        this.activeElements = [];
    }

    /**
     * 요소들의 탭 인덱스를 비활성화 (-1로 설정)
     * @param {HTMLElement[]} elements - 비활성화할 요소들
     */
    disable(elements) {
        if (!Array.isArray(elements)) {
            elements = [elements];
        }

        elements.forEach(element => {
            if (!element) return;

            // 이미 저장된 요소는 건너뛰기
            if (this.originalTabIndexes.has(element)) {
                return;
            }

            // 원본 tabindex 저장
            const originalTabIndex = element.getAttribute('tabindex');
            this.originalTabIndexes.set(element, originalTabIndex);

            // tabindex를 -1로 설정
            element.setAttribute('tabindex', '-1');
        });

        this.activeElements.push(...elements);
    }

    /**
     * 요소들의 탭 인덱스를 복원
     * @param {HTMLElement[]} elements - 복원할 요소들 (없으면 모든 활성 요소 복원)
     */
    enable(elements = null) {
        let elementsToRestore = elements || this.activeElements;

        if (!Array.isArray(elementsToRestore)) {
            elementsToRestore = [elementsToRestore];
        }

        elementsToRestore.forEach(element => {
            if (!element) return;

            const originalTabIndex = this.originalTabIndexes.get(element);
            
            if (originalTabIndex === null) {
                // 원래 tabindex가 없었던 경우 속성 제거
                element.removeAttribute('tabindex');
            } else {
                // 원래 tabindex로 복원
                element.setAttribute('tabindex', originalTabIndex);
            }

            this.originalTabIndexes.delete(element);
        });

        // 복원된 요소들을 활성 목록에서 제거
        if (elements) {
            this.activeElements = this.activeElements.filter(el => !elements.includes(el));
        } else {
            this.activeElements = [];
        }
    }

    /**
     * 모든 탭 인덱스 복원 및 초기화
     */
    reset() {
        this.enable();
        this.originalTabIndexes.clear();
    }

    /**
     * 컨테이너 내부의 모든 포커스 가능한 요소들의 탭 인덱스를 비활성화
     * @param {HTMLElement} container - 컨테이너 요소
     * @param {HTMLElement[]} exceptions - 제외할 요소들
     */
    disableContainer(container, exceptions = []) {
        if (!container) return;

        const focusableSelectors = [
            'a[href]',
            'button:not([disabled])',
            'textarea:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            '[tabindex]:not([tabindex="-1"])'
        ].join(', ');

        const focusableElements = Array.from(container.querySelectorAll(focusableSelectors))
            .filter(el => !exceptions.includes(el));

        this.disable(focusableElements);
    }

    /**
     * 컨테이너 내부의 모든 포커스 가능한 요소들의 탭 인덱스를 복원
     * @param {HTMLElement} container - 컨테이너 요소
     */
    enableContainer(container) {
        if (!container) return;

        const focusableSelectors = [
            'a[href]',
            'button:not([disabled])',
            'textarea:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            '[tabindex]'
        ].join(', ');

        const focusableElements = Array.from(container.querySelectorAll(focusableSelectors));
        this.enable(focusableElements);
    }
}

